// The chat of the text channel shown: what the server says of its messages — pages of them, new ones,
// edits, deletions, reactions, the users a message names — and what the user does: writes (the chat's
// Markdown, images, an answer to a message, users named), edits and deletes messages (its own; the host
// and moderators any), reacts with emoji, reads older ones, goes to a message an answer quotes.
//
// Images are made ready (images.ts) and uploaded in pieces over the connection the moment they are picked,
// pasted or dropped — a message waits for its images and goes once they are up. A connection made again
// uploads them again: the server forgets the files of a session that ended.
//
// In the box the user writes in, a user named is `@name`; in the text that goes it is `<@number>`, the
// server's form, which the chat shows as the user's name again.
import { canonical, tookEmoji } from "./emoji.ts";
import { NotAnImage, prepare, type Coded, type Ready } from "./images.ts";
import { mentions as named } from "./markdown.ts";
import { send } from "./net.ts";
import type { Server } from "./protocol.ts";
import {
  changed,
  isHost,
  isModerator,
  nameOf,
  show,
  state,
  type Attachment,
  type Draft,
  type Message,
} from "./store.ts";

/** The most messages the page keeps; older ones a page asks for. */
export const SHOWN = 500;
export const PAGE = 100;
/** A message number beyond any: `history` before it gives the newest messages. */
export const NEWEST = 2 ** 40;
/** The longest text, in characters, and the most images, of a message (the server's). */
const MOST_TEXT = 4000;
const MOST_FILES = 10;
/** How often the server hears the user types, ms. */
const TYPING_EVERY = 3000;
/** Pages older ones the page asks for at most to reach a message an answer quotes. */
const SEEK = 20;

/** The chat shown starts afresh: another channel's, or another meeting's. */
export function resetChat(): void {
  state.messages = new Map();
  state.last = 0;
  state.asking = false;
  state.all = false;
  state.editing = 0;
  state.jump = 0;
  state.selected = 0;
  state.typists = [];
  state.toEnd = true;
  seeking = 0;
}

/** What an answer quotes of the message it answers. */
export type Quote = NonNullable<Message["reply"]>;

/** Whether quote `q` is of a message deleted since: the server sends nothing of it but its number — no
 * text and no images, which a message has. */
export function quotesDeleted(q: Quote): boolean {
  return q.text === "" && q.files === 0;
}

/** Whether the middle shows text channel `channel`. */
function showing(channel: number): boolean {
  return state.meeting !== null && state.view.kind === "text" && state.view.channel === channel;
}

/** The server's messages of the chat. */
export type ChatMsg = Server<"page" | "message" | "edited" | "deleted" | "reacted" | "mentioned" | "uploaded" | "typing">;

/** What the server says of the chat. */
export function onChat(m: ChatMsg): void {
  switch (m.type) {
    case "page":
      return paged(m);
    case "message":
      return message(m);
    case "edited": {
      const x = showing(m.channel) ? state.messages.get(m.seq) : undefined;
      if (x === undefined) return;
      x.text = m.text;
      x.edited = m.at;
      return changed();
    }
    case "deleted": {
      if (!showing(m.channel)) return;
      state.messages.delete(m.seq);
      if (state.editing === m.seq) state.editing = 0;
      // The answers to it quote nothing of it any more, as the server sends them now; nor is it answered.
      for (const x of state.messages.values())
        if (x.reply !== null && x.reply.seq === m.seq) x.reply = { seq: m.seq, from: 0, name: "", text: "", files: 0 };
      const d = state.drafts.get(m.channel);
      if (d !== undefined && d.reply !== null && d.reply.seq === m.seq) d.reply = null;
      return changed();
    }
    case "reacted":
      return reacted(m);
    case "mentioned":
      // The message shows: it is seen. Elsewhere, its channel says it is there.
      if (showing(m.channel) && document.visibilityState === "visible") return;
      state.mentions.set(m.channel, (state.mentions.get(m.channel) ?? 0) + 1);
      return changed();
    case "uploaded":
      return uploaded(m);
    case "typing":
      if (!showing(m.channel)) return;
      state.typists = m.users.filter((id) => id !== state.user);
      return changed();
  }
}

/** Messages of the text channel shown: those after the newest the page had (on opening it), or older
 * ones it asked for — none of them, or the first of all: there are no older ones. */
function paged(m: Server<"page">): void {
  if (!showing(m.channel)) return;
  for (const x of m.messages) keep(x);
  if (state.asking && m.messages.length === 0) state.all = true;
  if (m.messages.some((x) => x.seq === 1)) state.all = true;
  state.asking = false;
  if (seeking > 0) seek();
  changed();
}

function message(m: Message): void {
  if (!showing(m.channel)) return;
  keep(m);
  // Who wrote no longer types.
  state.typists = state.typists.filter((id) => id !== m.from);
  if (m.from === state.user) {
    const p = state.pending.get(m.nonce);
    if (p !== undefined) {
      state.pending.delete(m.nonce);
      p.files.forEach((f) => URL.revokeObjectURL(f.url));
    }
    state.toEnd = true;
  }
  changed();
}

function keep(m: Message): void {
  state.messages.set(m.seq, m);
  state.last = Math.max(state.last, m.seq);
  // The page keeps the newest ones.
  if (state.messages.size > SHOWN * 2) {
    const kept = [...state.messages.keys()].sort((a, b) => a - b).slice(-SHOWN);
    state.messages = new Map(kept.map((k) => [k, state.messages.get(k)!]));
    state.all = false;
  }
}

/** A reaction given or taken back. */
function reacted(m: Server<"reacted">): void {
  const x = showing(m.channel) ? state.messages.get(m.seq) : undefined;
  if (x === undefined) return;
  const i = x.reactions.findIndex((r) => r.emoji === m.emoji);
  const had = i >= 0 ? x.reactions[i].users : [];
  const users = m.on ? (had.includes(m.user) ? had : [...had, m.user]) : had.filter((u) => u !== m.user);
  if (i < 0 && m.on) x.reactions = [...x.reactions, { emoji: m.emoji, users }];
  else if (i >= 0)
    x.reactions =
      users.length === 0
        ? x.reactions.filter((_, k) => k !== i)
        : x.reactions.map((r, k) => (k === i ? { emoji: m.emoji, users } : r));
  changed();
}

/** The number of the oldest message the page has (Infinity: none). */
function first(): number {
  let least = Infinity;
  for (const k of state.messages.keys()) least = Math.min(least, k);
  return least;
}

/** The view came near the oldest message: older ones are asked for. */
export function older(): void {
  if (state.asking || state.all || state.meeting === null || state.view.kind !== "text") return;
  const oldest = first();
  if (oldest === Infinity || oldest <= 1) return;
  state.asking = true;
  send({ type: "history", channel: state.view.channel, before: oldest, limit: PAGE });
}

/** How many more pages to ask for to reach message `jumpTo`'s, and which. */
let seeking = 0;
let sought = 0;

/** Go to message `seq` (an answer quotes it): shown, older pages asked for till it is. */
export function jumpTo(seq: number): void {
  if (state.messages.has(seq)) {
    state.jump = seq;
    return changed();
  }
  if (state.all || seq <= 0) return show("сообщение не найдено", true);
  sought = seq;
  seeking = SEEK;
  older();
}

function seek(): void {
  if (state.messages.has(sought)) {
    seeking = 0;
    state.jump = sought;
    return;
  }
  seeking--;
  if (seeking > 0 && !state.all) older();
  else {
    seeking = 0;
    show("сообщение слишком далеко в истории", true);
  }
}

/** The draft of channel `channel`, made the first time. */
export function draftOf(channel: number): Draft {
  let d = state.drafts.get(channel);
  if (d === undefined) {
    d = { text: "", reply: null, files: [], named: [] };
    state.drafts.set(channel, d);
  }
  return d;
}

/** The text of the box as it goes: the users named `@name` there as `<@number>`. */
export function wire(text: string, names: [string, number][]): string {
  const longest = [...names].sort((a, b) => b[0].length - a[0].length);
  return longest.reduce((t, [name, user]) => t.split(`@${name}`).join(`<@${user}>`), text);
}

/** Text `text` as the box shows it: the users named in it as `@name`, and who they are. */
export function boxed(text: string): [string, [string, number][]] {
  const users = named(text);
  const pairs: [string, number][] = users.map((u) => [nameOf(u), u]);
  return [text.replace(/<@(\d{1,16})>/g, (_, u: string) => `@${nameOf(Number(u))}`), pairs];
}

/** The user wrote `text` in the box of the text channel shown, and sends it with the draft's images and
 * answer: it shows at once, and goes when its images are up. */
export function say(text: string): void {
  if (state.meeting === null || state.view.kind !== "text") return;
  const channel = state.view.channel;
  const d = draftOf(channel);
  const t = wire(text.trim(), d.named);
  if (t === "" && d.files.length === 0) return;
  if ([...t].length > MOST_TEXT) return show("сообщение слишком длинное", true);
  if (d.files.some((f) => f.status === "failed")) return show("уберите изображения, которые не загрузились", true);
  const nonce = Math.floor(Math.random() * 2 ** 31) + 1;
  state.pending.set(nonce, { channel, text: t, files: d.files, reply: d.reply, waiting: true });
  state.drafts.set(channel, { text: "", reply: null, files: [], named: [] });
  state.toEnd = true;
  flush(nonce);
  changed();
}

/** Pending message `nonce` sent, if its images are up. */
function flush(nonce: number): void {
  const p = state.pending.get(nonce);
  if (p === undefined || !p.waiting || !p.files.every((f) => f.status === "done")) return;
  p.waiting = false;
  send({
    type: "say",
    channel: p.channel,
    text: p.text,
    files: p.files.map((f) => ({ file: f.file, preview: f.preview })),
    reply: p.reply?.seq ?? 0,
    nonce,
  });
}

/** The user types: the server hears it now and then. */
let typedAt = 0;
export function typed(): void {
  const now = performance.now();
  if (state.meeting === null || state.view.kind !== "text" || now - typedAt < TYPING_EVERY) return;
  typedAt = now;
  send({ type: "typing", channel: state.view.channel });
}

/** The draft answers message `m` (`null`: none). */
export function answer(m: Message | null): void {
  if (state.view.kind !== "text") return;
  draftOf(state.view.channel).reply = m;
  changed();
}

/** Whether the user may delete message `m`: its own, or any as the host or a moderator. */
export function mayDelete(m: Message): boolean {
  return m.from === state.user || isHost() || isModerator(state.user);
}

/** The user edits message `seq` (0: none any more). */
export function editing(seq: number): void {
  state.editing = seq;
  changed();
}

/** The user's message `m` with text `text` (as the box has it, users named `names`): an empty one asks
 * to delete the message, if it has no images. */
export function saveEdit(m: Message, text: string, names: [string, number][]): void {
  const t = wire(text.trim(), names);
  state.editing = 0;
  if (t === "" && m.files.length === 0) {
    state.dialog = { kind: "delete", message: m };
    return changed();
  }
  if ([...t].length > MOST_TEXT) return show("сообщение слишком длинное", true);
  if (t !== m.text) send({ type: "edit", channel: m.channel, seq: m.seq, text: t });
  changed();
}

/** The user's last message of the channel shown, to edit it (`ArrowUp` in an empty box). */
export function lastOwn(): Message | null {
  const own = [...state.messages.values()].filter((m) => m.from === state.user);
  return own.length === 0 ? null : own.reduce((a, b) => (a.seq > b.seq ? a : b));
}

/** Message `m` deleted — asked first, unless `now`. */
export function remove(m: Message, now = false): void {
  if (!now) {
    state.dialog = { kind: "delete", message: m };
    return changed();
  }
  state.dialog = null;
  send({ type: "delete", channel: m.channel, seq: m.seq });
  changed();
}

/** The user's reaction `emoji` to message `m`: given, or taken back if it was. */
export function react(m: Message, emoji: string): void {
  const e = canonical(emoji);
  const on = !m.reactions.some((r) => r.emoji === e && r.users.includes(state.user));
  if (on) tookEmoji(e);
  send({ type: "react", channel: m.channel, seq: m.seq, emoji: e, on });
}

// ---------------------------------------------------------------- images

/** Images being made ready or uploaded, by their key: what goes up. */
const readies = new Map<number, Ready>();
let nextKey = 1;

/** Files `files` (picked, pasted or dropped) among the images of the draft of the channel shown: made
 * ready and uploaded at once. */
export function attach(files: File[]): void {
  if (state.view.kind !== "text") return;
  const d = draftOf(state.view.channel);
  const room = MOST_FILES - d.files.length;
  if (room <= 0) return show(`в сообщении не больше ${MOST_FILES} изображений`, true);
  if (files.length > room) show(`в сообщении не больше ${MOST_FILES} изображений: лишние не добавлены`, true);
  for (const file of files.slice(0, room)) {
    const a: Attachment = {
      key: nextKey++,
      name: file.name || "изображение",
      url: "",
      width: 0,
      height: 0,
      size: file.size,
      status: "preparing",
      progress: 0,
      file: "",
      preview: "",
      error: "",
    };
    d.files = [...d.files, a];
    void ready(a, file);
  }
  changed();
}

async function ready(a: Attachment, file: File): Promise<void> {
  try {
    const r = await prepare(file);
    readies.set(a.key, r);
    a.name = r.name;
    a.url = URL.createObjectURL(r.preview?.blob ?? r.image.blob);
    a.width = r.image.width;
    a.height = r.image.height;
    a.size = r.image.blob.size + (r.preview?.blob.size ?? 0);
    void uploadOf(a);
  } catch (err) {
    a.status = "failed";
    a.error = err instanceof NotAnImage ? err.message : "не получилось подготовить изображение";
    changed();
  }
}

/** Image `a` uploaded — and its smaller copy — again if it was up before (a connection made again). An
 * image taken out of the draft (`progress` -1) is left as it is. */
async function uploadOf(a: Attachment): Promise<void> {
  const r = readies.get(a.key);
  if (r === undefined) return;
  a.status = "uploading";
  a.progress = 0;
  a.error = "";
  a.file = "";
  a.preview = "";
  changed();
  const total = r.image.blob.size + (r.preview?.blob.size ?? 0);
  const before = r.preview?.blob.size ?? 0;
  try {
    const preview = r.preview === null ? "" : await uploadCoded(r.preview, r.name, (n) => progress(a, n / total));
    const file = await uploadCoded(r.image, r.name, (n) => progress(a, (before + n) / total));
    if (a.progress < 0) return;
    a.file = file;
    a.preview = preview;
    a.status = "done";
    a.progress = 1;
    changed();
    for (const [nonce, p] of state.pending) if (p.files.includes(a)) flush(nonce);
  } catch (err) {
    if (a.progress < 0) return;
    a.status = "failed";
    a.error = (err as Error).message;
    changed();
    failedPending(a);
  }
}

function progress(a: Attachment, k: number): void {
  if (a.status !== "uploading" || a.progress < 0) return;
  a.progress = Math.min(0.99, k);
  changed();
}

/** A message waiting for image `a`, which did not go up: back in its draft, the image marked. */
function failedPending(a: Attachment): void {
  for (const [nonce, p] of state.pending) {
    if (!p.files.includes(a)) continue;
    state.pending.delete(nonce);
    const d = draftOf(p.channel);
    if (d.text === "" && d.files.length === 0) {
      const [text, names] = boxed(p.text);
      state.drafts.set(p.channel, { text, reply: p.reply, files: p.files, named: names });
    }
    show(`изображение «${a.name}» не загрузилось: ${a.error}`, true);
  }
}

/** Image `key` taken out of the draft of the channel shown. */
export function detach(key: number): void {
  if (state.view.kind !== "text") return;
  const d = draftOf(state.view.channel);
  const a = d.files.find((f) => f.key === key);
  if (a === undefined) return;
  a.progress = -1;
  d.files = d.files.filter((f) => f.key !== key);
  readies.delete(key);
  if (a.url !== "") URL.revokeObjectURL(a.url);
  changed();
}

/** The connection is up again (a new session), which knows no upload of the one before: those under way
 * start again from their first byte, and the images of drafts and of messages not sent that were up go
 * up again, as the server forgot them. */
export function rearm(): void {
  const going = [...jobs.values()];
  jobs.clear();
  for (const j of going) {
    j.at = 0;
    j.upload = nextUpload++;
    jobs.set(j.upload, j);
  }
  if (going.length > 0 && pumping === 0) pumping = window.setInterval(pump, TICK);
  const drafts = [...state.drafts.values()].flatMap((d) => d.files);
  const waiting = [...state.pending.values()].flatMap((p) => p.files);
  for (const a of [...drafts, ...waiting]) if (a.status === "done") void uploadOf(a);
}

/** The bytes of a piece of an upload: a frame of the WebSocket stays under the server's 128 KB. */
const PIECE = 48 * 1024;
/** Pieces sent every `TICK` ms at most — 300 a second, under the session's limit — and bytes waiting in
 * the WebSocket past which none are. */
const PIECES = 12;
const TICK = 40;
const BUFFERED = 2 * 1024 * 1024;

/** An upload under way: its number, bytes, format, name and pixels, how many bytes went, and what waits
 * for its answer. */
type Job = {
  upload: number;
  bytes: Uint8Array;
  coded: Coded;
  name: string;
  at: number;
  progress: (sent: number) => void;
  done: (file: string) => void;
  failed: (why: Error) => void;
};

const jobs = new Map<number, Job>();
let nextUpload = 1;
let pumping = 0;

/** Image `c` uploaded: the id the server gave it. */
async function uploadCoded(c: Coded, name: string, progress: (sent: number) => void): Promise<string> {
  const bytes = new Uint8Array(await c.blob.arrayBuffer());
  return new Promise((done, failed) => {
    const upload = nextUpload++;
    jobs.set(upload, { upload, bytes, coded: c, name, at: 0, progress, done, failed });
    if (pumping === 0) pumping = window.setInterval(pump, TICK);
    pump();
  });
}

/** Pieces of the uploads under way sent, each upload in turn; none while the connection is down or the
 * WebSocket holds much already. */
function pump(): void {
  const ws = state.ws;
  let sent = 0;
  let left = false;
  for (const j of jobs.values()) {
    if (j.at >= j.bytes.length) continue;
    left = true;
    if (!state.up || ws === null || ws.bufferedAmount > BUFFERED || sent >= PIECES) continue;
    while (j.at < j.bytes.length && sent < PIECES && ws.bufferedAmount <= BUFFERED) {
      const data = j.bytes.subarray(j.at, Math.min(j.bytes.length, j.at + PIECE));
      send({
        type: "upload",
        upload: j.upload,
        at: j.at,
        data,
        kind: j.coded.kind,
        size: j.bytes.length,
        name: j.name,
        width: j.coded.width,
        height: j.coded.height,
      });
      j.at += data.length;
      sent++;
      j.progress(j.at);
    }
  }
  if (!left && pumping !== 0) {
    clearInterval(pumping);
    pumping = 0;
  }
}

/** The server's answer to an upload's last piece, or why it did not take it. */
function uploaded(m: Server<"uploaded">): void {
  const j = jobs.get(m.upload);
  if (j === undefined) return;
  jobs.delete(m.upload);
  if (m.error === "") j.done(m.file);
  else j.failed(new Error(UPLOAD[m.error] ?? m.error));
}

/** Why the server did not take a file, in the user's words. */
const UPLOAD: Record<string, string> = {
  "files are off on this server": "на этом сервере нельзя загружать файлы",
  "only images: png, jpg, gif, webp": "можно загружать только изображения: PNG, JPEG, GIF, WebP",
  "the file is too large": "файл больше 10 МБ",
  "the file is not the image it says": "файл повреждён или это не изображение",
  "too many files at once": "слишком много файлов сразу — подождите, пока загрузятся",
  "the server could not keep the file": "сервер не смог сохранить файл",
  "the pieces do not fit": "файл загрузился с ошибкой — попробуйте ещё раз",
  "no such upload": "файл загрузился с ошибкой — попробуйте ещё раз",
  "too many files from your address: try again later": "слишком много файлов с вашего адреса — попробуйте позже",
};

/** Picture `c` (images.ts) uploaded and made the user's: everyone sees it. */
export async function setAvatar(c: Coded): Promise<void> {
  try {
    show("загружаю аватар…");
    const file = await uploadCoded(c, "avatar", () => {});
    send({ type: "avatar", file });
    state.avatar = file;
    show("аватар обновлён");
  } catch (err) {
    show(`аватар не загрузился: ${(err as Error).message}`, true);
  }
}

/** The user's picture taken away. */
export function dropAvatar(): void {
  send({ type: "avatar", file: "" });
  state.avatar = "";
  changed();
}
